/**
 * BaseAppNode - Unified keyboard deletion handling for all custom nodes
 *
 * This abstract base class provides automatic keyboard deletion support (Backspace/Delete)
 * for all custom application nodes that extend from it.
 *
 * Key features:
 * - Uses KEY_DOWN_COMMAND with event.key checking
 * - Uses COMMAND_PRIORITY_LOW (priority: 1) which is higher than COMMAND_PRIORITY_EDITOR (0)
 * - Uses type-based checking via isBaseAppNodeType() for unified type checking
 * - Integrates with nodeRemovalTracking to prevent scrollHeight issues during deletion
 *
 * Usage:
 * 1. Extend your custom node from BaseAppNode instead of DecoratorNode
 * 2. Call super(isInline, key) in constructor
 * 3. Register useBaseAppNodeDeletion() hook in LexicalEditor
 *
 * Example:
 * ```tsx
 * export class MyNode extends BaseAppNode {
 *   constructor(data, key?) {
 *     super(false, key);  // false = block-level node
 *     this.__data = data;
 *   }
 * }
 * ```
 */

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  DecoratorNode,
  ElementNode,
  KEY_DOWN_COMMAND,
  type LexicalNode,
  type NodeKey,
} from "lexical";
import { type JSX, useEffect } from "react";

/**
 * BaseAppNode - Abstract base class for all custom application nodes
 *
 * Provides unified keyboard deletion handling for block-level custom nodes.
 */
export abstract class BaseAppNode extends DecoratorNode<JSX.Element> {
  /** Whether this node is inline (default: false for block-level nodes) */
  private __isInline: boolean;

  constructor(isInline: boolean, key?: NodeKey) {
    super(key);
    this.__isInline = isInline;
  }

  /**
   * Specify whether this node is inline or block-level
   * @returns true if inline, false if block-level
   */
  override isInline(): boolean {
    return this.__isInline;
  }

  /**
   * BaseAppNode instances are always isolated (cannot merge with adjacent nodes)
   */
  override isIsolated(): boolean {
    return true;
  }

  /**
   * BaseAppNode instances are always keyboard selectable
   */
  override isKeyboardSelectable(): boolean {
    return true;
  }

  /**
   * Check if this node is a BaseAppNode instance
   * This is more reliable than instanceof in some module systems
   */
  isBaseAppNode(): boolean {
    return true;
  }
}

/**
 * List of BaseAppNode subclasses for type checking
 * This is needed because instanceof can fail in module systems
 */
const BASE_APP_NODE_TYPES = new Set<string>();

/**
 * Register a BaseAppNode subclass
 * This should be called in the static initializer of each BaseAppNode subclass
 */
export function registerBaseAppNodeType(type: string): void {
  BASE_APP_NODE_TYPES.add(type);
}

/**
 * Check if a given node type is a BaseAppNode subclass
 */
function isBaseAppNodeType(type: string): boolean {
  return BASE_APP_NODE_TYPES.has(type);
}

/**
 * Register keyboard deletion commands for all BaseAppNode instances
 *
 * This hook should be called once in LexicalEditor component to enable
 * keyboard deletion for all nodes extending BaseAppNode.
 *
 * Handles two scenarios:
 * 1. Backspace: When cursor is at beginning of an empty paragraph AFTER a BaseAppNode
 * 2. Delete: When cursor is at end of an empty paragraph BEFORE a BaseAppNode
 *
 * @example
 * ```tsx
 * function LexicalEditor() {
 *   const [editor] = useLexicalComposerContext();
 *   useBaseAppNodeDeletion();  // Register deletion commands
 *   // ...
 * }
 * ```
 */
export function useBaseAppNodeDeletion(): void {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    /**
     * Handle Backspace and Delete keys for BaseAppNode deletion
     */
    const unregister = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        // Handle both Delete and Backspace keys
        if (event.key !== "Delete" && event.key !== "Backspace") {
          return false;
        }

        // Check if we should delete a BaseAppNode
        const shouldDelete = editor.getEditorState().read(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
            return null;
          }

          const anchorNode = selection.anchor.getNode();
          const offset = selection.anchor.offset;

          // Get paragraph node - could be anchorNode itself (empty paragraph) or its parent (text node within paragraph)
          let paragraphNode: ElementNode | null = null;

          if ($isParagraphNode(anchorNode)) {
            // Cursor is directly on paragraph (empty paragraph case)
            paragraphNode = anchorNode;
          } else if ($isTextNode(anchorNode)) {
            // Cursor is in text node within paragraph
            const parentNode = anchorNode.getParent();
            if ($isParagraphNode(parentNode)) {
              paragraphNode = parentNode;
            } else {
              return null;
            }
          } else {
            return null;
          }

          if (!paragraphNode) {
            return null;
          }

          if (event.key === "Backspace") {
            // Scenario 1: Backspace - cursor at beginning of paragraph AFTER node
            // Delete previous BaseAppNode sibling
            if (offset === 0) {
              const prevSibling = paragraphNode.getPreviousSibling();
              if (prevSibling && isBaseAppNodeType(prevSibling.getType())) {
                return prevSibling;
              }
            }
          } else if (event.key === "Delete") {
            // Scenario 2: Delete - cursor at end of paragraph BEFORE node
            // Delete next BaseAppNode sibling
            let isAtEnd = false;
            if ($isTextNode(anchorNode)) {
              const textContent = anchorNode.getTextContent();
              isAtEnd = offset === textContent.length;
            } else if ($isParagraphNode(anchorNode)) {
              // Empty paragraph - cursor at offset 0 is both beginning and end
              isAtEnd = true;
            }

            if (isAtEnd) {
              const nextSibling = paragraphNode.getNextSibling();
              if (nextSibling && isBaseAppNodeType(nextSibling.getType())) {
                return nextSibling;
              }
            }
          }

          return null;
        });

        // If we found a node to delete, do it now
        if (shouldDelete) {
          event.preventDefault();
          event.stopPropagation();
          editor.update(() => {
            shouldDelete.remove();
          });
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_LOW,
    );

    // Cleanup function
    return () => {
      unregister();
    };
  }, [editor]);
}

/**
 * Type guard to check if a node is a BaseAppNode instance
 *
 * @example
 * ```tsx
 * if ($isBaseAppNode(node)) {
 *   // This node extends BaseAppNode
 * }
 * ```
 */
export function $isBaseAppNode(node: LexicalNode | null | undefined): node is BaseAppNode {
  return node instanceof BaseAppNode;
}
